import { useId, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field'
import { FormDialog, FormDialogContent, FormDialogTrigger } from '@/components/ui/form-dialog'
import { Input } from '@/components/ui/input'
import { apiClient } from '@/lib/api/client'
import { dueAtFromDateInput, dueDateInputValue, quickEditErrorMessage } from '@/lib/quick-edit'
import { DueDateNotice, type DueDateNoticeRecord } from '@/src/components/DueDateNotice'

/** "24 Oct 2026, 17:00", in Kampala. */
function kampalaDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Africa/Kampala',
  })
}

/**
 * A record's due date on its own page, with a way for admins to move or clear
 * it. Moving it offers to tell the customer, as quick edit does.
 *
 * `onChanged` should refresh the page behind it (useAsyncData's `refresh`),
 * not reload it: a reload replaces the page, and the offer with it.
 */
export function DueDateEditor({
  record,
  dueAt,
  canEdit,
  onChanged,
}: {
  record: DueDateNoticeRecord
  dueAt: string | null | undefined
  canEdit: boolean
  onChanged: () => void
}) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [moved, setMoved] = useState<{ previousDueAt: string | null; dueAt: string } | null>(null)
  // What was saved, shown until the page reloads.
  const [saved, setSaved] = useState<{ dueAt: string | null } | null>(null)
  const shown = saved ? saved.dueAt : dueAt

  function openChange(next: boolean) {
    if (saving) return
    if (next) {
      setDate(dueDateInputValue(shown))
      setError(null)
    }
    setOpen(next)
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    if (date === dueDateInputValue(shown)) {
      setOpen(false)
      return
    }
    const next = dueAtFromDateInput(date, shown)
    setSaving(true)
    setError(null)
    try {
      if (record.kind === 'consignment') {
        await apiClient.updateConsignment(record.id, { sla_due_at: next })
      } else {
        await apiClient.updateInquiry(record.id, { sla_due_at: next })
      }
      setOpen(false)
      setSaved({ dueAt: next })
      if (next) setMoved({ previousDueAt: shown ?? null, dueAt: next })
      onChanged()
    } catch (cause) {
      setError(quickEditErrorMessage(cause))
    } finally {
      setSaving(false)
    }
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span>{shown ? kampalaDateTime(shown) : 'Not set'}</span>
      {canEdit && (
        <FormDialog open={open} onOpenChange={openChange}>
          <FormDialogTrigger asChild>
            <Button type="button" variant="outline" size="xs">
              Change
            </Button>
          </FormDialogTrigger>
          <FormDialogContent
            size="sm"
            title="Due date"
            description={record.reference}
            submitLabel="Save"
            busy={saving}
            error={error}
            onSubmit={save}
          >
            <Field>
              <FieldLabel htmlFor={`${id}-due`}>Due date</FieldLabel>
              <div className="flex gap-2">
                <Input
                  id={`${id}-due`}
                  type="date"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                  className="min-w-0 flex-1"
                  aria-describedby={`${id}-due-help`}
                />
                {date && (
                  <Button type="button" variant="outline" onClick={() => setDate('')}>
                    Clear
                  </Button>
                )}
              </div>
              <FieldDescription id={`${id}-due-help`}>
                Due by 17:00 Kampala time, unless it already has a time.
              </FieldDescription>
            </Field>
          </FormDialogContent>
        </FormDialog>
      )}
      {moved && (
        <DueDateNotice
          open
          onOpenChange={(next) => {
            if (next) return
            setMoved(null)
            onChanged()
          }}
          record={record}
          previousDueAt={moved.previousDueAt}
          dueAt={moved.dueAt}
        />
      )}
    </span>
  )
}
